import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@shared/utils/utils';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn('fixed bottom-0 left-0 z-[100] flex max-h-screen w-full flex-col p-4 sm:max-w-[355px]', className)}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border border-card-border shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-left-full data-[state=open]:slide-in-from-left-full bg-card-bg',
  {
    variants: {
      variant: {
        default: 'border text-text',
        destructive: 'destructive group dark:border-[#4D151B] text-error',
        success: 'success group dark:border-[#1E331E] text-success',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

// Componente para la barra de progreso
const ToastProgressBar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    duration?: number;
    createdAt?: number;
    variant?: string;
  }
>(({ className, duration = 5000, createdAt, variant, ...props }, ref) => {
  const [progress, setProgress] = React.useState(100);

  React.useEffect(() => {
    if (!createdAt) return;

    const startTime = createdAt;
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - startTime;
      const remaining = Math.max(0, duration - elapsed);
      const newProgress = (elapsed / duration) * 100;

      setProgress(newProgress);

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 50); // Actualizar cada 50ms para animación fluida

    return () => clearInterval(interval);
  }, [duration, createdAt]);

  return (
    <div
      ref={ref}
      className={cn(
        'absolute top-0 left-0 h-1 w-full',
        variant === 'destructive' ? 'bg-destructive/20' : variant === 'success' ? 'bg-success/20' : 'bg-primary/20',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'h-full transition-all duration-75 ease-linear',
          variant === 'destructive' ? 'bg-error' : variant === 'success' ? 'bg-success' : 'bg-primary',
        )}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
});
ToastProgressBar.displayName = 'ToastProgressBar';

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> & {
      duration?: number;
      createdAt?: number;
    }
>(({ className, variant, duration, createdAt, ...props }, ref) => {
  return (
    <ToastPrimitives.Root ref={ref} className={cn(toastVariants({ variant }), 'pb-2', className)} {...props}>
      <ToastProgressBar duration={duration} createdAt={createdAt} variant={variant ?? 'default'} />
      <div className="p-3 pb-2 pl-2 pr-6 w-full border-card-border text-text">
        {React.Children.map(props.children, (child) => {
          if (
            React.isValidElement<{ variant?: string }>(child) &&
            child.type &&
            ((child.type as any).displayName === 'ToastDescription' || (child.type as any).displayName === 'ToastTitle')
          ) {
            return React.cloneElement(child, { variant: variant ?? 'default' });
          }
          return child;
        })}
      </div>
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-xs border bg-transparent px-2 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]',
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'absolute right-2 top-4 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:!text-text focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600',
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-5 w-5" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title> & {
    variant?: string;
  }
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      '!text-base font-semibold [&+div]:text-sm pt-2',
      variant === 'destructive' && 'text-error',
      variant === 'success' && 'text-success',
      className,
    )}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description> & {
    variant?: string;
  }
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn(
      'text-complementary-text !text-sm whitespace-pre-line',
      variant === 'destructive' && 'text-error',
      variant === 'success' && 'text-success',
      className,
    )}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastActionElement,
  type ToastProps,
};
